import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { useSelector } from "react-redux"
import { isLegacyFightCategory } from 'web/data/core-data'
import { Fight, FightEvent, FightSegment, FullFight } from "web/data/models"
import { loadMockNzothFight } from "web/data/yaml-importer"
import { OutlineFactory } from 'web/engine/outline-factory'
import { sortSegments } from 'web/utils/sort-utils'
import { expectOneById, removeFromArrayById } from "web/utils/utils"
import { RootState } from "../store"

export type FightState = {
    fights: Fight[]
    segments: FightSegment[]
    events: FightEvent[]
}

const initialState: FightState = ((): FightState => {
    const data = loadMockNzothFight()

    return {
        fights: [data.fight],
        events: [...data.events],
        segments: [...data.segments],
    }

})()

const createFight = createAsyncThunk<
    { fullFight: FullFight },
    { name: string; fightId: string; categoryId: string },
    { state: RootState }
>(
    "fight/createFight",
    ({ name, fightId, categoryId }, thunkApi) => {

        const fight = OutlineFactory.createFight({
            fightName: name,
            fightCategoryId: categoryId,
            fightId
        })

        // The first segment is required and owned by the system
        const firstSegment = OutlineFactory.createSegment({
            fightId: fight.id,
            name: "Segment 1",
            offset: 0,
            userCreated: false,
        })

        // First event is required and owned by the system
        const firstEvent = OutlineFactory.createEvent({
            fightSegmentId: firstSegment.id,
            name: "Pull",
            fightOffset: 0,
            userCreated: false,
            flags: []
        })

        thunkApi.dispatch(FightActions.addNewFight({
            fight,
            events: [firstEvent],
            segments: [firstSegment]
        }))

        return {
            fullFight: selectors(thunkApi.getState().fight).getFullFight(fightId)
        }
    }
)

const addDummyFight = createAsyncThunk<
    { fightId: string },
    void,
    { state: RootState }
>(
    "fight/addDummyFight",
    (_, thunkApi) => {
        const data = loadMockNzothFight()

        thunkApi.dispatch(FightActions.addNewFight({
            fight: data.fight,
            segments: data.segments,
            events: data.events
        }))

        return {
            fightId: data.fight.id
        }
    }
)

const slice = createSlice({
    name: "fight",
    initialState,
    reducers: {
        addNewFight: (state, action: PayloadAction<{
            fight: Fight
            segments: FightSegment[]
            events: FightEvent[]
        }>) => {
            state.fights.push(action.payload.fight)
            state.segments.push(...action.payload.segments)
            state.events.push(...action.payload.events)
        },

        deleteFight: (state, action: PayloadAction<{ fightId: string }>) => {
            const segmentsToDelete = state.segments.filter(s => s.fightId === action.payload.fightId)
            const eventsToDelete = state.events.filter(
                e => segmentsToDelete.find(s => s.id === e.fightSegmentId)
            )

            state.fights = state.fights.filter(f => f.id !== action.payload.fightId)
            state.segments = removeFromArrayById(state.segments, segmentsToDelete)
            state.events = removeFromArrayById(state.events, eventsToDelete)
        },

        updateFight: (state, action: PayloadAction<{
            fight: Fight
            segments: FightSegment[]
            events: FightEvent[]
        }>) => {

            const { fight: immutableFight, segments, events } = action.payload

            // The fight object coming in here is likely an immutable
            // immer instance, need to spread it into a fresh object.
            const updatedFight = {
                ...immutableFight
            }

            updatedFight.updatedAt = new Date().toISOString()

            const fightIndex = state.fights.findIndex(fi => fi.id === updatedFight.id)
            state.fights[fightIndex] = updatedFight

            const oldSegments = state.segments.filter(seg => seg.fightId === updatedFight.id)
            const oldEvents = state.events.filter(ev => {
                return oldSegments.some(seg => seg.id === ev.fightSegmentId)
            })

            // Kill old segments
            state.segments = state.segments.filter(seg => seg.fightId !== updatedFight.id)
            // Kill old events
            state.events = state.events.filter(ev => oldEvents.some(oldEv => oldEv.id === ev.id) === false)

            // Add new stuff
            state.segments.push(...segments)
            state.events.push(...events)
        },

    }
})

function selectors(state: FightState) {
    return {
        /**
         * Returns all parts of the active fight
         */
        getFullFight: (fightId: string, setupEvents?: FightEvent[]): FullFight => {
            const segments = state.segments
                .filter(s => s.fightId === fightId)
                .sort(sortSegments)

            const events = state.events
                .filter(ev => segments
                    .findIndex(seg => seg.id === ev.fightSegmentId) >= 0)

            if (setupEvents != null) {
                // This has no function, it's just a data integrity
                // check because I'm paranoid
                setupEvents.forEach(ev => {
                    expectOneById(segments, ev.fightSegmentId)
                })

                events.push(...setupEvents)
            }

            return {
                fight: expectOneById(state.fights, fightId),
                segments,
                events
            }
        },

        /**
         * Returns all fights that belong to outdated fight
         * categories.
         */
        getLegacyFights: (): Fight[] => {
            return state.fights.filter(fight =>
                isLegacyFightCategory(fight.categoryId))
        }

    }
}

export const FightActions = { ...slice.actions, createFight, addDummyFight }
export const FightReducer = slice.reducer
export const FightSelectors = selectors

export const useFightState = () => useSelector<RootState, FightState>(state => state.fight)

export const useFightSelectors = () => {
    const state = useSelector<RootState, FightState>(state => state.fight)
    return selectors(state)
}
